import useLeaveDetection from "@/hooks/useLeaveDetection"
import { toTitleCase } from "@/utils/str"
import { useMemo, useState } from "react"
import { useApp } from "../../AppProvider"
import { useSyncLabReports } from "../../hooks/useLabReports"
import { Button, Spinner } from "../common"

interface BackgroundSyncProps {
  organizationId: string
}

interface SyncProgress {
  provider: "ANTECH" | "IDEXX" | "ZOETIS"
  totalChunks: number
  completedChunks: number
  status: "idle" | "running" | "completed" | "error" | "cancelled"
  errorMessage?: string
  totalReportsFound: number
  startDate: string
  endDate: string
  chunkResults: ChunkResult[]
}

interface ChunkResult {
  index: number
  startDate: string
  endDate: string
  status: "pending" | "running" | "completed" | "error" | "cancelled"
  reportsFound: number
  errorMessage?: string
}

export const BackgroundSync = ({ organizationId }: BackgroundSyncProps) => {
  const { token } = useApp()
  const [currentSync, setCurrentSync] = useState<SyncProgress | null>(null)
  const [selectedProvider, setSelectedProvider] = useState<"ANTECH" | "IDEXX" | "ZOETIS">("ANTECH")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [abortController, setAbortController] = useState<AbortController | null>(null)

  const syncMutation = useSyncLabReports(organizationId, token)

  const isRunning = useMemo(() => currentSync?.status === "running", [currentSync])
  useLeaveDetection(isRunning)

  const formatDate = (dateString: string) => {
    // Parse date string directly to avoid timezone issues
    const [year, month, day] = dateString.split("-").map(Number)
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(year, month - 1, day))
  }

  const generateWeeklyChunks = (start: string, end: string) => {
    const chunks: Array<{ startDate: Date; endDate: Date }> = []
    const startDateObj = new Date(start)

    // End date should be the start of the next day (end of the selected day)
    const endDateObj = new Date(end)
    endDateObj.setDate(endDateObj.getDate() + 1) // Add one day to make it end of day

    let currentStart = new Date(startDateObj)

    while (currentStart < endDateObj) {
      const chunkStart = new Date(currentStart)
      const chunkEnd = new Date(currentStart)
      chunkEnd.setDate(chunkEnd.getDate() + 7) // Add 7 days for a week

      // Don't go past the end date or the current date
      const now = new Date()
      const maxDate = endDateObj < now ? endDateObj : now
      if (chunkEnd > maxDate) {
        chunkEnd.setTime(endDateObj.getTime())
      }

      chunks.push({
        startDate: chunkStart,
        endDate: chunkEnd,
      })

      // Move to next week (start where this chunk ended)
      currentStart = new Date(chunkEnd)
    }

    return chunks
  }

  const handleStartSync = async () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates")
      return
    }

    if (new Date(startDate) >= new Date(endDate)) {
      alert("Start date must be before end date")
      return
    }

    if (new Date(endDate) >= new Date()) {
      alert("End date must be in the past")
      return
    }

    const chunks = generateWeeklyChunks(startDate, endDate)

    // Create abort controller for this sync
    const controller = new AbortController()
    setAbortController(controller)

    // Initialize weekly results
    const chunkResults: ChunkResult[] = chunks.map((chunk, index) => ({
      index: index + 1,
      startDate: chunk.startDate.toISOString().split("T")[0],
      endDate: chunk.endDate.toISOString().split("T")[0],
      status: "pending" as const,
      reportsFound: 0,
    }))

    // Initialize progress tracking
    setCurrentSync({
      provider: selectedProvider,
      totalChunks: chunks.length,
      completedChunks: 0,
      status: "running",
      totalReportsFound: 0,
      startDate,
      endDate,
      chunkResults,
    })

    try {
      for (let i = 0; i < chunks.length; i++) {
        if (controller.signal.aborted) {
          return
        }

        const chunk = chunks[i]

        // Only update status if not cancelled
        setCurrentSync((prev) =>
          prev && prev.status !== "cancelled"
            ? {
                ...prev,
                chunkResults: prev.chunkResults.map((result: ChunkResult, index: number) =>
                  index === i ? { ...result, status: "running" as const } : result,
                ),
              }
            : prev,
        )

        try {
          const result = await syncMutation.mutateAsync({
            provider: selectedProvider,
            startDate: chunk.startDate.toISOString().split("T")[0],
            endDate: chunk.endDate.toISOString().split("T")[0],
          })

          if (controller.signal.aborted) {
            return
          }

          setCurrentSync((prev) =>
            prev && prev.status !== "cancelled"
              ? {
                  ...prev,
                  completedChunks: i + 1,
                  totalReportsFound: prev.totalReportsFound + result.newReports,
                  chunkResults: prev.chunkResults.map((weekResult: ChunkResult, index: number) =>
                    index === i
                      ? {
                          ...weekResult,
                          status: "completed" as const,
                          reportsFound: result.newReports,
                        }
                      : weekResult,
                  ),
                }
              : prev,
          )
        } catch (chunkError) {
          console.error(`Error in chunk ${i + 1}:`, chunkError)

          if (controller.signal.aborted) {
            return
          }

          // Only update status if not cancelled
          setCurrentSync((prev) =>
            prev && prev.status !== "cancelled"
              ? {
                  ...prev,
                  chunkResults: prev.chunkResults.map((weekResult: ChunkResult, index: number) =>
                    index === i
                      ? {
                          ...weekResult,
                          status: "error" as const,
                          errorMessage: chunkError instanceof Error ? chunkError.message : "Unknown error",
                        }
                      : weekResult,
                  ),
                }
              : prev,
          )

          throw chunkError
        }

        if (controller.signal.aborted) {
          return
        }

        if (i < chunks.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 1000))
        }
      }

      if (controller.signal.aborted) {
        return
      }

      setCurrentSync((prev) =>
        prev && prev.status !== "cancelled"
          ? {
              ...prev,
              status: "completed" as const,
              completedChunks: chunks.length,
            }
          : prev,
      )

      // Clear the abort controller
      setAbortController(null)
    } catch (error) {
      console.error("Sync failed:", error)

      if (controller.signal.aborted) {
        return
      }

      setCurrentSync((prev) =>
        prev && prev.status !== "cancelled"
          ? {
              ...prev,
              status: "error" as const,
              errorMessage: error instanceof Error ? error.message : "Unknown error",
            }
          : prev,
      )

      // Clear the abort controller
      setAbortController(null)
    }
  }

  const handleCancelSync = () => {
    if (currentSync && abortController) {
      // Abort the ongoing sync
      abortController.abort()

      setCurrentSync((prev) =>
        prev
          ? {
              ...prev,
              status: "cancelled" as const,
              errorMessage: "Sync was cancelled by user",
              chunkResults: prev.chunkResults.map((result: ChunkResult) => {
                // Mark pending and running chunks as cancelled
                if (result.status === "pending" || result.status === "running") {
                  return { ...result, status: "cancelled" as const }
                }
                return result
              }),
            }
          : null,
      )

      // Clear the abort controller
      setAbortController(null)
    }
  }

  return (
    <div className="">
      <h3 className="text-lg font-semibold text-gray-900">Sync lab reports</h3>
      <p className="text-sm text-gray-600">Please keep your browser open while syncs are running</p>

      {currentSync ? (
        <div className="mb-6">
          <div className="mt-4 mb-3 rounded-lg border border-gray-100 bg-gray-50 p-4">
            <div className="flex justify-between">
              <div className="mb-3">
                <h4 className="text-md font-medium text-gray-900">Syncing {currentSync.provider}</h4>
                <div className="mt-1 text-sm text-gray-600">
                  {formatDate(currentSync.startDate)} to {formatDate(currentSync.endDate)}
                </div>
                <div className="mt-1 text-sm text-gray-600">
                  Reports found: {currentSync.totalReportsFound}
                </div>
              </div>
              {currentSync.status === "running" ? (
                <Button variant="outline" onClick={handleCancelSync} className="btn-outline">
                  Cancel
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    setCurrentSync(null)
                    setAbortController(null)
                  }}
                  className="btn">
                  Start new sync
                </Button>
              )}
            </div>

            <div className="mb-2 flex items-center gap-2">
              {currentSync.status === "running" && (
                <div className="h-4 w-4">
                  <Spinner />
                </div>
              )}
              <span
                className={`text-sm font-medium ${
                  currentSync.status === "cancelled"
                    ? "text-orange-600"
                    : currentSync.status === "error"
                      ? "text-red-600"
                      : currentSync.status === "completed"
                        ? "text-green-600"
                        : "text-gray-600"
                }`}>
                Status: {toTitleCase(currentSync.status)}
              </span>
              {currentSync.status === "cancelled" && (
                <span className="text-xs text-orange-600">• Stopped by user</span>
              )}
            </div>

            {currentSync.totalChunks > 0 && (
              <div className="h-2 w-full rounded-full bg-gray-200">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${
                    currentSync.status === "cancelled"
                      ? "bg-orange-500"
                      : currentSync.status === "error"
                        ? "bg-red-500"
                        : currentSync.status === "completed"
                          ? "bg-green-500"
                          : "bg-primary-600"
                  }`}
                  style={{
                    width: `${(currentSync.completedChunks / currentSync.totalChunks) * 100}%`,
                  }}
                />
              </div>
            )}

            <div className="mt-2 text-xs text-gray-500">
              {currentSync.completedChunks} of {currentSync.totalChunks} weeks completed
            </div>

            {currentSync.chunkResults.length > 0 && (
              <div className="mt-4">
                <h5 className="mb-2 text-sm font-medium text-gray-700">Sync progress</h5>
                <div className="overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr>
                        <th className="py-1 pr-2 text-left font-medium text-gray-600">Chunk</th>
                        <th className="px-2 py-1 text-left font-medium text-gray-600">Date Range</th>
                        <th className="px-2 py-1 text-left font-medium text-gray-600">Status</th>
                        <th className="px-2 py-1 text-left font-medium text-gray-600">Reports</th>
                        <th className="py-1 pl-2 text-left font-medium text-gray-600">Error</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {currentSync.chunkResults.map((result) => (
                        <tr key={result.index}>
                          <td className="py-1 pr-2 text-gray-700">{result.index}</td>
                          <td className="px-2 py-1 text-gray-600">
                            {formatDate(result.startDate)} - {formatDate(result.endDate)}
                          </td>
                          <td className="px-2 py-1">
                            <span
                              className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${
                                result.status === "completed"
                                  ? "bg-green-100 text-green-800"
                                  : result.status === "running"
                                    ? "bg-blue-100 text-blue-800"
                                    : result.status === "cancelled"
                                      ? "bg-orange-100 text-orange-800"
                                      : result.status === "error" || currentSync.status === "error"
                                        ? "bg-red-100 text-red-800"
                                        : "bg-gray-100 text-gray-600"
                              }`}>
                              {result.status === "cancelled"
                                ? "Cancelled"
                                : currentSync.status === "error" && result.status === "pending"
                                  ? "Error"
                                  : toTitleCase(result.status)}
                            </span>
                          </td>
                          <td className="px-2 py-1 text-gray-700">{result.reportsFound}</td>
                          <td className="py-1 pl-2 text-xs text-red-600">
                            {result.errorMessage && (
                              <span className="block break-words" title={result.errorMessage}>
                                {result.errorMessage}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-4 mb-6 grid grid-cols-1 gap-4 rounded-lg md:grid-cols-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Provider</label>
            <select
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value as any)}
              className="focus:border-primary-500 focus:ring-primary-500 w-full rounded-md border-gray-300">
              <option value="ANTECH">Antech</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="focus:border-primary-500 focus:ring-primary-500 w-full rounded-md border-gray-300"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="focus:border-primary-500 focus:ring-primary-500 w-full rounded-md border-gray-300"
            />
          </div>
          <div className="flex items-end">
            <Button onClick={handleStartSync} className="btn h-11">
              Start Sync
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
